import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Screen } from '../types';
import api from '../../../services/api';

const AadhaarEKYCScreen = ({ navigateTo, setUserData }) => {
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [step, setStep] = useState('input'); // 'input', 'loading', 'digilocker', 'fetching'
  const [error, setError] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const digilockerWindowRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (digilockerWindowRef.current && !digilockerWindowRef.current.closed) {
        digilockerWindowRef.current.close();
      }
    };
  }, []);

  // Extract user details from Digilocker response
  const extractUserDetails = (details) => {
    if (!details) return null;
    
    // Handle both flattened and nested user_details structure
    const userDetails = details.user_details || details;
    
    const name = userDetails.name || details.name || details.poi?.name || details.full_name || '';
    const dob = userDetails.dob || details.dob || details.poi?.dob || details.date_of_birth || '';
    const gender = userDetails.gender || details.gender || details.poi?.gender || '';
    const address = details.address || details.poa?.full || 
      [details.poa?.house, details.poa?.street, details.poa?.loc, details.poa?.dist, details.poa?.state, details.poa?.pc]
        .filter(Boolean).join(', ') || '';
    const fatherName = details.care_of || details.poa?.co || details.father_name || '';
    const uid = details.uid || details.uid_number || details.aadhaar_number || '';
    const photoUrl = details.photo || details.poi?.photo || details.photo_link || details.photoUrl || '';
    const mobile = userDetails.mobile || details.mobile || '';

    return {
      name,
      aadhaar: uid ? `XXXX-XXXX-${uid.slice(-4)}` : '',
      fatherName,
      dob,
      gender: gender === 'M' ? 'Male' : gender === 'F' ? 'Female' : gender,
      address,
      photoUrl: photoUrl ? (photoUrl.startsWith('data:') ? photoUrl : `data:image/jpeg;base64,${photoUrl}`) : null,
      mobile: mobile ? `+91 ${mobile}` : ''
    };
  };

  // Poll for Digilocker status
  const pollDigilockerStatus = useCallback(async (vId, refId) => {
    try {
      const response = await api.get(`/api/kyc/digilocker/status/${vId}`, {
        params: refId ? { reference_id: refId } : {}
      });

      const status = response.data?.status;
      const isVerified = status === 'COMPLETED' || status === 'AUTHENTICATED';

      if (response.data?.success && isVerified) {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Close Digilocker window if still open
        if (digilockerWindowRef.current && !digilockerWindowRef.current.closed) {
          digilockerWindowRef.current.close();
        }

        // Check if we already have user details from status response
        const statusDetails = response.data?.details;
        if (statusDetails?.name || statusDetails?.user_details?.name) {
          // Use details from status response directly
          const extractedData = extractUserDetails(statusDetails);
          if (extractedData) {
            setUserData({
              ...extractedData,
              verificationId: vId,
            });
            // Still fetch the DigiLocker document so the backend stores the Aadhaar image
            // (required for face compare).
            setStep('fetching');
            setLoadingMessage('Fetching your Aadhaar details...');
            await fetchDigilockerDocument(vId, refId);
            return;
          }
        }

        // Otherwise fetch the document
        setStep('fetching');
        setLoadingMessage('Fetching your Aadhaar details...');
        await fetchDigilockerDocument(vId, refId);
      } else if (status === 'FAILED' || status === 'EXPIRED') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setError('Verification failed or expired. Please try again.');
        setStep('input');
      }
    } catch (err) {
      console.error('Status poll error:', err);
    }
  }, []);

  // Fetch Digilocker document
  const fetchDigilockerDocument = async (vId, refId) => {
    try {
      const response = await api.post('/api/kyc/digilocker/document', {
        verificationId: vId,
        referenceId: refId,
        documentType: 'AADHAAR'
      });

      if (response.data?.success) {
        const details = response.data?.details;
        const extractedData = extractUserDetails(details);
        
        if (extractedData) {
          setUserData({
            ...extractedData,
            verificationId: vId,
            documentUrl: response.data?.documentUrl
          });
          navigateTo(Screen.ApplicantDetails);
        } else {
          setError('Could not extract user details. Please try again.');
          setStep('input');
        }
      } else if (response.data?.code === 'validation_pending') {
        // Retry after a short delay
        setLoadingMessage('Validation in progress, please wait...');
        setTimeout(() => fetchDigilockerDocument(vId, refId), 3000);
      } else {
        setError(response.data?.message || 'Failed to fetch document. Please try again.');
        setStep('input');
      }
    } catch (err) {
      console.error('Document fetch error:', err);
      setError(err?.response?.data?.message || 'Failed to fetch Aadhaar details. Please try again.');
      setStep('input');
    }
  };

  // Start Digilocker verification
  const handleStartDigilocker = async () => {
    if (aadhaarNumber.length !== 12) return;

    setError('');
    setStep('loading');
    setLoadingMessage('Connecting to DigiLocker...');

    try {
      // Safari blocks popups if window.open() is not directly tied to the click event.
      // Open a blank popup synchronously, then set its location once we have the redirect URL.
      const width = 500;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      if (!digilockerWindowRef.current || digilockerWindowRef.current.closed) {
        digilockerWindowRef.current = window.open(
          'about:blank',
          'DigiLocker',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
        );
      }

      // Use the current origin/path so the callback returns into the same environment
      // (dev/staging/prod) and the same embedding context.
      const redirectUrl = `${window.location.origin}${window.location.pathname}?callback=digilocker`;

      const response = await api.post('/api/kyc/digilocker/start', {
        aadhaarNumber,
        redirectUrl
      });

      if (response.data?.success && response.data?.redirectUrl) {
        const vId = response.data.verificationId;
        const refId = response.data.referenceId;
        
        setVerificationId(vId);
        setReferenceId(refId);
        setStep('digilocker');
        setLoadingMessage('Complete verification in DigiLocker window...');

        // Navigate the popup we opened earlier (or try opening now as a fallback).
        if (digilockerWindowRef.current && !digilockerWindowRef.current.closed) {
          try {
            digilockerWindowRef.current.location.href = response.data.redirectUrl;
            try { digilockerWindowRef.current.focus(); } catch (_) { /* ignore */ }
          } catch (_) {
            // If navigation fails, fall back to a new tab.
            digilockerWindowRef.current = window.open(
              response.data.redirectUrl,
              'DigiLocker',
              `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
            );
          }
        } else {
          digilockerWindowRef.current = window.open(
            response.data.redirectUrl,
            'DigiLocker',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
          );
        }

        // If popups are blocked (common on iOS Safari or inside iframes),
        // redirect in the same tab as a last resort.
        if (!digilockerWindowRef.current) {
          window.location.href = response.data.redirectUrl;
          return;
        }

        // Start polling for status
        pollingIntervalRef.current = setInterval(() => {
          pollDigilockerStatus(vId, refId);
          
          // Also check if popup was closed manually
          if (digilockerWindowRef.current && digilockerWindowRef.current.closed) {
            // Give a bit more time for status to update, then check once more
            setTimeout(() => {
              pollDigilockerStatus(vId, refId);
            }, 2000);
          }
        }, 3000);

      } else {
        setError(response.data?.message || 'Failed to start verification. Please try again.');
        setStep('input');
      }
    } catch (err) {
      console.error('Start Digilocker error:', err);
      setError(err?.response?.data?.message || 'Failed to connect to DigiLocker. Please try again.');
      setStep('input');
    }
  };

  // Handle cancel/retry
  const handleCancel = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (digilockerWindowRef.current && !digilockerWindowRef.current.closed) {
      digilockerWindowRef.current.close();
    }
    setStep('input');
    setError('');
    setVerificationId(null);
    setReferenceId(null);
  };

  // Aadhaar input screen
  if (step === 'input') {
    return (
      <div className="ilp-screen">
        <div className="ilp-header">
          <button className="ilp-back-btn" onClick={() => navigateTo(Screen.VerificationMethod)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2>Aadhaar Verification</h2>
          <div className="ilp-header-spacer"></div>
        </div>

        <div className="ilp-ekyc-content">
          <div className="ilp-ekyc-intro">
            <div className="ilp-digilocker-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1>DigiLocker Verification</h1>
            <p>Enter your Aadhaar number to verify via DigiLocker. You will be redirected to complete secure authentication.</p>
          </div>

          {error && (
            <div className="ilp-error-message">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="ilp-input-group">
            <label>12-digit Aadhaar Number</label>
            <input
              type="text"
              placeholder="Enter Aadhaar number"
              value={aadhaarNumber}
              onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
              maxLength={12}
              className={aadhaarNumber.length === 12 ? 'valid' : ''}
            />
            {aadhaarNumber.length > 0 && aadhaarNumber.length < 12 && (
              <span className="ilp-input-hint">{12 - aadhaarNumber.length} more digits required</span>
            )}
          </div>

          <div className="ilp-digilocker-info">
            <div className="ilp-info-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="#10b981">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Secure government-backed verification</span>
            </div>
            <div className="ilp-info-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="#10b981">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No OTP required - authenticate via DigiLocker</span>
            </div>
            <div className="ilp-info-item">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="#10b981">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Auto-fill details from verified Aadhaar</span>
            </div>
          </div>
        </div>

        <div className="ilp-ekyc-footer">
          <button 
            className={`ilp-primary-btn full ${aadhaarNumber.length !== 12 ? 'disabled' : ''}`}
            disabled={aadhaarNumber.length !== 12}
            onClick={handleStartDigilocker}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Verify with DigiLocker
          </button>
          <div className="ilp-secure-note">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span>Powered by Cashfree & DigiLocker</span>
          </div>
        </div>
      </div>
    );
  }

  // Loading/Digilocker/Fetching screen
  return (
    <div className="ilp-screen">
      <div className="ilp-header">
        <button className="ilp-back-btn" onClick={handleCancel}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2>Aadhaar Verification</h2>
        <div className="ilp-header-spacer"></div>
      </div>

      <div className="ilp-ekyc-content centered">
        <div className="ilp-digilocker-loading">
          {step === 'fetching' ? (
            <div className="ilp-success-animation">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          ) : (
            <div className="ilp-loading-spinner large">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.2" />
                <path d="M12 2a10 10 0 0110 10" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                </path>
              </svg>
            </div>
          )}

          <h2>{step === 'digilocker' ? 'Complete Verification' : loadingMessage}</h2>
          
          {step === 'digilocker' && (
            <div className="ilp-digilocker-instructions">
              <p>A DigiLocker window has been opened. Please complete the verification there.</p>
              <div className="ilp-steps-list">
                <div className="ilp-step-item">
                  <span className="ilp-step-number">1</span>
                  <span>Sign in to DigiLocker</span>
                </div>
                <div className="ilp-step-item">
                  <span className="ilp-step-number">2</span>
                  <span>Authorize Aadhaar access</span>
                </div>
                <div className="ilp-step-item">
                  <span className="ilp-step-number">3</span>
                  <span>Return here automatically</span>
                </div>
              </div>
              <button className="ilp-secondary-btn" onClick={handleCancel}>
                Cancel Verification
              </button>
            </div>
          )}

          {(step === 'loading' || step === 'fetching') && (
            <p className="ilp-loading-subtext">Please wait, this may take a few moments...</p>
          )}
        </div>
      </div>

      <div className="ilp-ekyc-footer">
        <div className="ilp-secure-note">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span>Secured by Cashfree & DigiLocker</span>
        </div>
      </div>
    </div>
  );
};

export default AadhaarEKYCScreen;
